import cn from 'classnames';
import React from 'react';
import ReactModal from 'react-modal';

import Icon, { IconTypes } from 'components/basic/Icon';
import { H1, H2 } from 'components/basic/Typography';

import type {
  IModalProps,
  ModalCSSProperties,
  ModalInnerComponentProps,
} from './types';

import s from './Modal.module.scss';

// Make sure to bind modal to your appElement (http://reactcommunity.org/react-modal/accessibility/)
ReactModal.setAppElement('#__next');

export const Modal: React.FC<IModalProps> = props => {
  const {
    className,
    children,
    style,
    title,
    subtitle,
    width,
    height,
    isOpen,
    onRequestClose,
    isFillSize,
    ...restProps
  } = props;
  const customContentStyle: ModalCSSProperties = {};

  if (width) {
    customContentStyle['--modal-width'] = width;
  }

  if (height) {
    customContentStyle['--modal-height'] = height;
  }

  return (
    <ReactModal
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      portalClassName={s.root}
      overlayClassName={s.overlay}
      className={cn(s.modal, 'shadowLevel2', className, {
        [s.fullSize]: isFillSize,
      })}
      style={{
        overlay: {
          top: globalThis.scrollY || globalThis.pageYOffset,
          ...style?.overlay,
        },
        content: {
          ...customContentStyle,
          ...style?.content,
        },
      }}
      {...restProps}
    >
      <Icon
        icon={IconTypes.close}
        size={24}
        color="white"
        className={cn(s.closeButton, { [s.closeButtonFullSize]: isFillSize })}
        onClick={onRequestClose}
      />

      {title && <ModalTitle title={title} subtitle={subtitle} />}

      <ModalContent>{children}</ModalContent>
    </ReactModal>
  );
};

export function ModalTitle({
  title,
  subtitle,
  className,
  ...restProps
}: ModalInnerComponentProps): JSX.Element {
  return (
    <header
      className={cn(s.header, className)}
      aria-labelledby="modal-title"
      {...restProps}
    >
      <H1 className={cn(s.headerTitle, 'cy-modal-title')} id="modal-title">
        {title}
      </H1>

      {subtitle && <H2 className={s.h2}>{subtitle}</H2>}
    </header>
  );
}

export function ModalContent({
  children,
  className,
}: ModalInnerComponentProps): JSX.Element {
  return <div className={cn(s.content, className)}>{children}</div>;
}
